import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminLink from '../components/AdminLink';
import { apiFetch } from '../jscontrol/apiClient';
import { pickRecordId, filterActiveRecords } from '../jscontrol/recordUtils';
import '../css/Category.css';

const UserPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const [filterText, setFilterText] = useState('');
  const [sortKey, setSortKey] = useState('FirstName');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [PAGE_SIZE, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const showToast = (message, type = 'danger') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchUsers = async (currentPage, pageSize, search, sk, sd) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        pageIndex: currentPage,
        pageSize: pageSize,
        search: search || '',
        sortColumn: sk,
        sortBy: sd.toUpperCase(),
      });
      const { data } = await apiFetch(`/User/GetAll?${params}`);
      const items = filterActiveRecords(data?.data ?? data?.Data ?? []);
      const count = data?.totalRecords ?? data?.totalCount ?? items.length;
      setUsers(items);
      setTotalCount(count);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(page, PAGE_SIZE, filterText, sortKey, sortDir);
  }, [page, PAGE_SIZE, filterText, sortKey, sortDir]);

  const handleDelete = async (user) => {
    const id = pickRecordId(user, 'UserId', 'userId');
    if (!id) {
      showToast('Could not delete — invalid user id.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await apiFetch(`/User/Delete/${id}`, { method: 'DELETE' });
      showToast('User deleted successfully.', 'success');
      setUsers((prev) => prev.filter((u) => pickRecordId(u, 'UserId', 'userId') !== id));
      setTotalCount((prev) => Math.max(0, prev - 1));
      fetchUsers(page, PAGE_SIZE, filterText, sortKey, sortDir);
    } catch (e) {
      showToast(e.message);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const goToPage = (p) => setPage(Math.max(1, Math.min(p, totalPages)));
  const handleFilter = (val) => { setFilterText(val); setPage(1); };
  const handlePageSize = (val) => { setPageSize(Number(val)); setPage(1); };

  const handleSortCol = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
    setPage(1);
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <i className="mdi mdi-sort ms-1" style={{ opacity: 0.3 }}></i>;
    return sortDir === 'asc'
      ? <i className="mdi mdi-sort-ascending ms-1" style={{ color: '#6f42c1' }}></i>
      : <i className="mdi mdi-sort-descending ms-1" style={{ color: '#6f42c1' }}></i>;
  };

  const formatDate = (val) => {
    if (!val) return '—';
    return new Date(val).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const renderPages = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(2, safePage - delta);
    const right = Math.min(totalPages - 1, safePage + delta);
    pages.push(
      <button key={1} className={`cat-page-btn${safePage === 1 ? ' active' : ''}`} onClick={() => goToPage(1)}>1</button>
    );
    if (left > 2) pages.push(<span key="l-ellipsis" className="cat-page-ellipsis">…</span>);
    for (let i = left; i <= right; i++) {
      pages.push(
        <button key={i} className={`cat-page-btn${safePage === i ? ' active' : ''}`} onClick={() => goToPage(i)}>{i}</button>
      );
    }
    if (right < totalPages - 1) pages.push(<span key="r-ellipsis" className="cat-page-ellipsis">…</span>);
    if (totalPages > 1) pages.push(
      <button key={totalPages} className={`cat-page-btn${safePage === totalPages ? ' active' : ''}`} onClick={() => goToPage(totalPages)}>{totalPages}</button>
    );
    return pages;
  };

  return (
    <AdminLayout>
      <p style={{ margin: '0 0 12px 0', fontSize: '0.85rem', color: '#6c757d' }}>
        <AdminLink to="/admin">Dashboard</AdminLink>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>Users</span>
      </p>

      <div className="row">
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              {toast && (
                <div className={`alert alert-${toast.type} alert-dismissible d-flex align-items-center py-2 mb-3`} role="alert" style={{ fontSize: '0.875rem' }}>
                  <i className={`mdi ${toast.type === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle'} me-2`} style={{ fontSize: '1.1rem' }}></i>
                  <span>{toast.message}</span>
                  <button type="button" className="btn-close ms-auto" style={{ fontSize: '0.7rem' }} onClick={() => setToast(null)} aria-label="Close" />
                </div>
              )}

              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h4 className="card-title mb-0">Users</h4>
                <div className="d-flex align-items-center gap-2">
                  <div className="cat-filter-wrap">
                    <i className="mdi mdi-magnify cat-filter-icon"></i>
                    <input
                      type="text"
                      className="cat-filter-input"
                      placeholder="Search user..."
                      value={filterText}
                      onChange={e => handleFilter(e.target.value)}
                    />
                  </div>
                  <button className="cat-add-btn" onClick={() => navigate('/admin/user/create')}>
                    <i className="mdi mdi-plus" style={{ marginRight: 6 }}></i> Add User
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : error ? (
                <div className="alert alert-danger d-flex align-items-center py-2" style={{ fontSize: '0.875rem' }}>
                  <i className="mdi mdi-alert-circle me-2" style={{ fontSize: '1.1rem' }}></i>
                  {error}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th className="cat-sort-th" onClick={() => handleSortCol('FirstName')} style={{ width: '18%' }}>
                          Name <SortIcon col="FirstName" />
                        </th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('Email')} style={{ width: '20%' }}>
                          Email <SortIcon col="Email" />
                        </th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('Username')} style={{ width: '12%' }}>
                          Username <SortIcon col="Username" />
                        </th>
                        <th style={{ width: '10%' }}>Email Verified</th>
                        <th style={{ width: '10%' }}>Blocked</th>
                        <th style={{ width: '10%' }}>Role</th>
                        <th className="cat-sort-th" onClick={() => handleSortCol('LastLoginAt')} style={{ width: '12%' }}>
                          Last Login <SortIcon col="LastLoginAt" />
                        </th>
                        <th style={{ width: '10%' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-4 text-muted">No users found.</td>
                        </tr>
                      ) : users.map((user) => {
                        const userId = pickRecordId(user, 'UserId', 'userId');
                        return (
                        <tr key={userId ?? user.Email ?? user.email}>
                          <td className="cat-name-cell">
                            {[user.FirstName, user.LastName].filter(Boolean).join(', ') || '—'}
                          </td>
                          <td>{user.Email || '—'}</td>
                          <td>{user.Username || '—'}</td>
                          <td>
                            <label className={`badge ${user.IsEmailVerified ? 'badge-success' : 'badge-danger'}`}>
                              {user.IsEmailVerified ? 'Yes' : 'No'}
                            </label>
                          </td>
                          <td>
                            <label className={`badge ${user.IsBlocked ? 'badge-danger' : 'badge-success'}`}>
                              {user.IsBlocked ? 'Yes' : 'No'}
                            </label>
                          </td>
                          <td>{user.RoleName || user.Role || '—'}</td>
                          <td>{formatDate(user.LastLoginAt)}</td>
                          <td>
                            {!(user.Username === 'admin' && (user.RoleName === 'Admin' || user.Role === 'Admin')) && (
                              <>
                                <button
                                  className="btn btn-sm btn-outline-primary me-1"
                                  style={{ border: 'none' }}
                                  onClick={() => navigate(`/admin/user/edit/${userId}`)}
                                  title="Edit"
                                >
                                  <i className="mdi mdi-pencil"></i>
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  style={{ border: 'none' }}
                                  onClick={() => handleDelete(user)}
                                  title="Delete"
                                >
                                  <i className="mdi mdi-delete"></i>
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              )}

              {!loading && !error && totalCount > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="cat-page-info">Rows per page:</span>
                    <select className="cat-pagesize-select" value={PAGE_SIZE} onChange={e => handlePageSize(e.target.value)}>
                      <option value={10}>10</option>
                      <option value={30}>30</option>
                      <option value={50}>50</option>
                    </select>
                    <span className="cat-page-info">
                      Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, totalCount)} of {totalCount}
                    </span>
                  </div>
                  <div className="cat-pagination">
                    <button className="cat-page-btn" onClick={() => goToPage(1)} disabled={safePage === 1} title="First">
                      <i className="mdi mdi-chevron-double-left"></i>
                    </button>
                    <button className="cat-page-btn" onClick={() => goToPage(safePage - 1)} disabled={safePage === 1} title="Previous">
                      <i className="mdi mdi-chevron-left"></i>
                    </button>
                    {renderPages()}
                    <button className="cat-page-btn" onClick={() => goToPage(safePage + 1)} disabled={safePage === totalPages} title="Next">
                      <i className="mdi mdi-chevron-right"></i>
                    </button>
                    <button className="cat-page-btn" onClick={() => goToPage(totalPages)} disabled={safePage === totalPages} title="Last">
                      <i className="mdi mdi-chevron-double-right"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserPage;
